import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Play, Scissors, Star, Trophy, Building2, Users, UserCircle, MessageSquare, Film, ChevronRight, AlertTriangle, Calendar, MapPin, Clock } from "lucide-react";
import AVModal from "@/components/AVModal";

const getMiddleNav = (role) => {
  switch (role) {
    case "ysv_admin":
      return { label: "Leagues", icon: Trophy, path: "/admin", color: "var(--cyan)" };
    case "league_admin":
      return { label: "Orgs", icon: Building2, path: "/admin", color: "#9c27b0" };
    case "org_admin":
      return { label: "Teams", icon: Users, path: "/admin", color: "#4caf50" };
    case "coach":
      return { label: "Players", icon: UserCircle, path: "/home", color: "#2196f3" };
    case "player_parent":
    default:
      return { label: "Players", icon: UserCircle, path: "/home", color: "#2196f3" };
  }
};

const HomePage = () => {
  const { user, api } = useAuth();
  const [stats, setStats] = useState({ games: 0, my_clips: 0, highlights: 0, leagues: 0 });
  const [games, setGames] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [teammates, setTeammates] = useState([]);
  const [profile, setProfile] = useState(null);
  const [showAV, setShowAV] = useState(false);

  const loadProfile = () => {
    api().get("/profile/full").then(r => setProfile(r.data)).catch(() => {});
  };

  useEffect(() => {
    api().get("/dashboard/stats").then(r => setStats(r.data)).catch(() => {});
    api().get("/games").then(r => setGames(r.data.slice(0, 5))).catch(() => {});
    api().get("/schedules").then(r => setSchedules(r.data)).catch(() => {});
    loadProfile();
    if (user?.role === "coach") {
      api().get("/users").then(r => setTeammates(r.data.filter(u => u.id !== user.id))).catch(() => {});
    }
  }, [api, user]);

  const formatDuration = (s) => `${Math.floor(s/60)}m`;

  // Filter schedules by the user's hierarchy. Backend returns all schedules for
  // approved users; we scope client-side for presentation. YSV/league admins see
  // league-wide; org_admins see their org's games; coach/player see their team's.
  const mySchedules = (() => {
    const role = user?.role;
    let list = schedules;
    if (role === "league_admin" && user?.league_id) {
      list = list.filter(s => s.league_id === user.league_id);
    } else if (role === "org_admin" && user?.org_id) {
      list = list.filter(s => (profile?.org_teams || []).some(t => t.id === s.home_team_id || t.id === s.away_team_id));
    } else if ((role === "coach" || role === "player_parent") && user?.team_id) {
      list = list.filter(s => s.home_team_id === user.team_id || s.away_team_id === user.team_id);
    }
    // Upcoming first (scheduled / pending), then completed — max 5
    const upcoming = list.filter(s => s.status !== "completed")
      .sort((a, b) => (a.game_date || "").localeCompare(b.game_date || ""));
    const done = list.filter(s => s.status === "completed")
      .sort((a, b) => (b.game_date || "").localeCompare(a.game_date || ""));
    return [...upcoming, ...done].slice(0, 5);
  })();

  return (
    <div data-testid="home-page" style={{ padding: '32px 40px' }}>
      {/* Welcome */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#ff6b00', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: '#fff' }}>
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Welcome back,</p>
            <h1 data-testid="welcome-name" style={{ fontFamily: "'Outfit', sans-serif", fontSize: 24, fontWeight: 700 }}>{user?.name}</h1>
            <span style={{ background: 'rgba(0, 229, 255, 0.15)', color: 'var(--cyan)', fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 12 }}>{user?.role?.replace('_', ' ').toUpperCase()}</span>
          </div>
        </div>
      </div>

      {/* AV Banner — shown for player_parent when AV is enabled and not yet verified */}
      {profile?.av_enabled && (user?.role === "player_parent" || user?.role === "coach") && user?.av_status !== "verified" && (
        <button data-testid="av-banner" onClick={() => setShowAV(true)} style={{
          width: '100%', padding: '14px 20px', background: 'rgba(255,152,0,0.12)',
          border: '2px solid rgba(255,152,0,0.4)', borderRadius: 12, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, textAlign: 'left',
        }}>
          <AlertTriangle size={22} style={{ color: '#ff9800', flexShrink: 0 }} />
          <div>
            <p style={{ fontSize: 15, fontWeight: 700, color: '#ff9800' }}>Complete Age Verification</p>
            <p style={{ fontSize: 12, color: '#cc8a00' }}>Required by your league. Tap to submit verification documents.</p>
          </div>
        </button>
      )}
      {showAV && <AVModal onClose={() => setShowAV(false)} onSuccess={loadProfile} />}

      {/* Quick Nav Cards - role-specific middle card */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
        {[
          { label: "Games", icon: Film, path: "/games", color: "#ff6b00" },
          getMiddleNav(user?.role),
          { label: "Messages", icon: MessageSquare, path: "/messages", color: "#4caf50" },
        ].map(item => (
          <Link key={item.label} to={item.path} data-testid={`quick-nav-${item.label.toLowerCase()}`} className="glass-card" style={{
            padding: '20px 24px', textDecoration: 'none', textAlign: 'center',
          }}>
            <item.icon size={28} style={{ color: item.color, marginBottom: 8 }} />
            <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{item.label}</p>
          </Link>
        ))}
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
        {[
          { label: "Games", value: stats.games, icon: Play, color: "#ff6b00" },
          { label: "My Clips", value: stats.my_clips, icon: Scissors, color: "var(--cyan)" },
          { label: "Highlights", value: stats.highlights, icon: Star, color: "var(--cyan)" },
        ].map(s => (
          <div key={s.label} className="glass-card" data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`} style={{ padding: '20px 24px', textAlign: 'center' }}>
            <s.icon size={22} style={{ color: s.color, marginBottom: 8 }} />
            <p style={{ fontSize: 32, fontWeight: 800, fontFamily: "'Outfit', sans-serif", color: 'var(--cyan)' }}>{s.value}</p>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Schedule (hierarchy-aware) */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 data-testid="home-schedule-heading" style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Calendar size={18} style={{ color: 'var(--cyan)' }} /> Schedule
          </h2>
          {user?.role !== "player_parent" && (
            <Link to="/admin" data-testid="home-schedule-manage" style={{ color: 'var(--cyan)', fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>Manage</Link>
          )}
        </div>
        {mySchedules.length === 0 ? (
          <div data-testid="home-schedule-empty" className="glass-card" style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
            No games scheduled yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {mySchedules.map(s => {
              const isDone = s.status === "completed";
              return (
                <div key={s.id} data-testid={`home-schedule-${s.id}`} className="glass-card" style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 18px', flexWrap: 'wrap', gap: 10,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0, flex: '1 1 280px' }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 10,
                      background: isDone ? 'rgba(76,175,80,0.12)' : 'rgba(0,229,255,0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <Calendar size={18} style={{ color: isDone ? '#4caf50' : 'var(--cyan)' }} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {s.home_team_name} <span style={{ color: 'var(--text-secondary)' }}>vs</span> {s.away_team_name}
                        {isDone && typeof s.home_score === "number" && (
                          <span style={{ fontSize: 12, marginLeft: 8, color: '#4caf50', fontWeight: 700 }}>
                            ({s.home_score}–{s.away_score})
                          </span>
                        )}
                      </p>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, flexWrap: 'wrap' }}>
                        {s.game_date && <span><Clock size={11} style={{ marginRight: 4, verticalAlign: -2 }} />{s.game_date}{s.game_time ? ` · ${s.game_time}` : ""}</span>}
                        {s.location && <span><MapPin size={11} style={{ marginRight: 4, verticalAlign: -2 }} />{s.location}</span>}
                        {s.age_group && <span style={{ fontSize: 11, background: 'rgba(255,107,0,0.15)', color: '#ff6b00', padding: '1px 8px', borderRadius: 10, fontWeight: 600 }}>{s.age_group}</span>}
                      </div>
                    </div>
                  </div>
                  <span style={{
                    fontSize: 11, padding: '3px 10px', borderRadius: 10, fontWeight: 600,
                    background: isDone ? 'rgba(76,175,80,0.12)' : 'rgba(255,152,0,0.12)',
                    color: isDone ? '#4caf50' : '#ff9800',
                  }}>{isDone ? "Completed" : "Upcoming"}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Games */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 700 }}>Recent Games</h2>
          <Link to="/games" data-testid="see-all-games" style={{ color: 'var(--cyan)', fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>See All</Link>
        </div>
        {games.map(game => (
          <Link key={game.id} to={`/games/${game.id}`} data-testid={`game-card-${game.id}`} className="glass-card" style={{
            display: 'flex', alignItems: 'center', padding: '14px 20px',
            marginBottom: 8, textDecoration: 'none', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: 10, background: 'rgba(255,107,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <Play size={20} style={{ color: '#ff6b00' }} />
                <span style={{ position: 'absolute', bottom: -4, right: -4, background: 'var(--cyan)', color: '#000', fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4 }}>{formatDuration(game.duration_seconds)}</span>
              </div>
              <div>
                <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{game.title}</p>
                <p style={{ fontSize: 12, color: 'var(--cyan)' }}>vs {game.opponent}</p>
              </div>
            </div>
            <ChevronRight size={18} style={{ color: 'var(--text-secondary)' }} />
          </Link>
        ))}
        {games.length === 0 && <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', padding: 40 }}>No games yet</p>}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Quick Actions</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {[
            { label: "Watch Games", icon: Play, path: "/games", color: "#ff6b00" },
            { label: "Create Clip", icon: Scissors, path: "/clips", color: "var(--cyan)" },
            { label: "Highlight Tape", icon: Star, path: "/highlights", color: "var(--cyan)" },
          ].map(a => (
            <Link key={a.label} to={a.path} data-testid={`action-${a.label.toLowerCase().replace(' ', '-')}`} className="glass-card" style={{
              padding: 24, textDecoration: 'none', textAlign: 'center',
            }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: `${a.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <a.icon size={22} style={{ color: a.color }} />
              </div>
              <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{a.label}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Team Players - shown for coach only (player/parent home page intentionally
          does not list other users — clips and team rosters live elsewhere). */}
      {user?.role === "coach" && teammates.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Team Players</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {teammates.map(t => (
              <div key={t.id} data-testid={`teammate-${t.id}`} className="glass-card" style={{
                padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', background: '#2196f3',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0,
                }}>{t.name?.charAt(0)}</div>
                <div style={{ overflow: 'hidden' }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</p>
                  <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t.role?.replace('_', ' ')}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
